import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { 
  Users, Shield, Settings, Activity, Download, 
  UserPlus, Trash2, Key, Eye, EyeOff, RefreshCw,
  BarChart3, Clock, Ticket, Building2, ListChecks, Plus, Edit2, ChevronDown, ChevronUp,
  FileText, Upload, Image, Wifi, MapPin, Cable, Trophy, Calendar, Wrench, Phone,
  FolderOpen, Smartphone, Bell, Wallet, Briefcase
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import GroupManagement from '../components/GroupManagement';
import ConnectionsPanel from '../components/ConnectionsPanel';
import {
  StatisticsFilters,
  TechnicianLeaderboard,
  OperatorLeaderboard,
  ServiceStatsCards,
  ServiceStatusBreakdown,
  ConnectionStatsCards,
  ConnectionStatusBreakdown,
  D2DStatsCards,
  D2DTopAgents,
  ScoringRules,
  AdminManagementPanel,
  AuditLogsPanel,
  SettingsPanel,
  UserManagementPanel,
  TechnicianPerformancePanel
} from '../components/dashboard';
import MapPanel from '../components/dashboard/MapPanel';
import HRPanel from '../components/dashboard/HRPanel';
import WorkZonesPanel from '../components/dashboard/WorkZonesPanel';
import AppVersionPanel from '../components/dashboard/AppVersionPanel';
import BroadcastPanel from '../components/dashboard/BroadcastPanel';
import AccountingPanel from '../components/dashboard/AccountingPanel';
import ModuleAccessPanel from '../components/dashboard/ModuleAccessPanel';
import NotificationBell from '../components/dashboard/NotificationBell';
import TopBarTasksButton from '../components/tasks/TopBarTasksButton';
import ProjectsPanel from '../components/projects/ProjectsPanel';
import TasksPanel from '../components/tasks/TasksPanel';
import StageManagementPanel from '../components/tasks/StageManagementPanel';
import RecurringTasksPanel from '../components/tasks/RecurringTasksPanel';
import OnlinePanel from '../components/network/OnlinePanel';
import OnlineConfigPanel from '../components/network/OnlineConfigPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'internet', label: 'İnternet' },
  { value: 'iptv', label: 'IPTV' },
  { value: 'catv', label: 'CATV' },
  { value: 'telefon', label: 'Telefon' }
];

const EQUIPMENT_CATEGORIES = [
  { value: 'ont_router', label: 'ONT / Router' },
  { value: 'iptv', label: 'IPTV' },
  { value: 'ip_phone', label: 'IP Telefon' }
];

export default function SuperAdminDashboard({ user, onLogout }) {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [settings, setSettings] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Statistics state
  const [statistics, setStatistics] = useState(null);
  const [connectionStats, setConnectionStats] = useState(null);
  const [d2dStats, setD2dStats] = useState(null);
  const [technicianScores, setTechnicianScores] = useState([]);
  const [operatorScores, setOperatorScores] = useState([]);
  const [allGroups, setAllGroups] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState(['all']);
  const [period, setPeriod] = useState('current_month');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [statsSubTab, setStatsSubTab] = useState('services');
  
  // Problem Types state
  const [problemTypes, setProblemTypes] = useState({});
  const [expandedCategory, setExpandedCategory] = useState('internet');
  const [showCreateProblemType, setShowCreateProblemType] = useState(false);
  const [showEditProblemType, setShowEditProblemType] = useState(false);
  const [selectedProblemType, setSelectedProblemType] = useState(null);
  const [newProblemType, setNewProblemType] = useState({ category: 'internet', code: '', name: '', description: '', order: 0 });
  
  // Contract Template state
  const [contractTemplate, setContractTemplate] = useState(null);
  const [tariffPlans, setTariffPlans] = useState([]);
  const [equipmentOptions, setEquipmentOptions] = useState([]);
  const [showCreateTariff, setShowCreateTariff] = useState(false);
  const [showEditTariff, setShowEditTariff] = useState(false);
  const [selectedTariff, setSelectedTariff] = useState(null);
  const [newTariff, setNewTariff] = useState({ name: '', speed_mbps: 0, price_azn: 0, description: '', order: 0 });
  const [showCreateEquipment, setShowCreateEquipment] = useState(false);
  const [showEditEquipment, setShowEditEquipment] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [newEquipment, setNewEquipment] = useState({ category: 'ont_router', name: '', specs: '', price_azn: 0 });
  const [contractSection, setContractSection] = useState('company'); // company, fees, tariffs, equipment, terms
  
  // ABillS state
  const [abillsConfig, setAbillsConfig] = useState({ server_url: '', api_key: '', connected: false });
  
  // Dialogs
  const [showCreateAdmin, setShowCreateAdmin] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // User Management state (for Users tab)
  const [showCreateUserDialog, setShowCreateUserDialog] = useState(false);
  const [showEditUserDialog, setShowEditUserDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [newUser, setNewUser] = useState({ username: '', password: '', full_name: '', phone: '', email: '', role: 'operator', modules: [] });
  const [editUser, setEditUser] = useState({ full_name: '', phone: '', email: '' });
  const [userNewPassword, setUserNewPassword] = useState('');
  
  // Form states
  const [newAdmin, setNewAdmin] = useState({ username: '', password: '', full_name: '', email: '', role: 'admin' });
  const [newPassword, setNewPassword] = useState('');
  
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  
  useEffect(() => {
    fetchData();
  }, [activeTab]);
  
  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'dashboard') {
        const res = await axios.get(`${API}/superadmin/dashboard-stats`, { headers });
        setStats(res.data);
      } else if (activeTab === 'statistics') {
        await fetchStatistics();
      } else if (activeTab === 'admins') {
        const res = await axios.get(`${API}/superadmin/all-admins`, { headers });
        setAdmins(res.data);
      } else if (activeTab === 'audit') {
        const res = await axios.get(`${API}/superadmin/audit-logs?limit=100`, { headers });
        setAuditLogs(res.data.logs);
      } else if (activeTab === 'settings') {
        const res = await axios.get(`${API}/superadmin/settings`, { headers });
        setSettings(res.data);
      } else if (activeTab === 'users') {
        const res = await axios.get(`${API}/admin/users`, { headers });
        setUsers(res.data);
      } else if (activeTab === 'problemTypes') {
        const res = await axios.get(`${API}/problem-types/by-category?active_only=false`, { headers });
        setProblemTypes(res.data);
      } else if (activeTab === 'contract') {
        const [templateRes, tariffsRes, equipmentRes] = await Promise.all([
          axios.get(`${API}/contract/template`, { headers }),
          axios.get(`${API}/contract/tariffs`, { headers }),
          axios.get(`${API}/contract/equipment`, { headers })
        ]);
        setContractTemplate(templateRes.data);
        setTariffPlans(tariffsRes.data);
        setEquipmentOptions(equipmentRes.data);
      }
    } catch (error) {
      toast.error('Məlumatları yükləmək mümkün olmadı');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchStatistics = async () => {
    try {
      // Fetch all groups
      const groupsRes = await axios.get(`${API}/groups`, { headers });
      setAllGroups(groupsRes.data);
      
      // Build query params
      const params = new URLSearchParams();
      if (period && period !== 'custom') {
        params.append('period', period);
      }
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (selectedGroupIds.length > 0 && !selectedGroupIds.includes('all')) {
        params.append('group_ids', selectedGroupIds.join(','));
      }
      
      const queryString = params.toString() ? '?' + params.toString() : '';
      
      const [statsRes, connStatsRes, techScoresRes, opScoresRes, d2dStatsRes] = await Promise.all([
        axios.get(`${API}/admin/statistics${queryString}`, { headers }),
        axios.get(`${API}/connections/stats${queryString}`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/admin/technician-scores${queryString}`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/operator-scores${queryString}`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/d2d/stats${queryString}`, { headers }).catch(() => ({ data: null }))
      ]);
      
      setStatistics(statsRes.data);
      setConnectionStats(connStatsRes.data);
      setTechnicianScores(techScoresRes.data || []);
      setOperatorScores(opScoresRes.data || []);
      setD2dStats(d2dStatsRes.data);
    } catch (error) {
      console.error('Statistics fetch error:', error);
    }
  };
  
  useEffect(() => {
    if (activeTab === 'statistics') {
      fetchStatistics();
    }
  }, [period, startDate, endDate, selectedGroupIds]);
  
  // Contract Template handlers
  const handleUpdateContractTemplate = async (field, value) => {
    try {
      await axios.put(`${API}/contract/template`, { [field]: value }, { headers });
      setContractTemplate(prev => ({ ...prev, [field]: value }));
      toast.success('Yeniləndi');
    } catch (error) {
      toast.error('Yeniləmək mümkün olmadı');
    }
  };
  
  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await axios.post(`${API}/contract/template/logo`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      setContractTemplate(prev => ({ ...prev, company_logo_url: res.data.logo_url }));
      toast.success('Logo yükləndi');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Logo yükləmək mümkün olmadı');
    }
  };
  
  const handleCreateTariff = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/contract/tariffs`, newTariff, { headers });
      toast.success('Tarif planı yaradıldı');
      setShowCreateTariff(false);
      setNewTariff({ name: '', speed_mbps: 0, price_azn: 0, description: '', order: 0 });
      fetchData();
    } catch (error) {
      toast.error('Tarif planı yaratmaq mümkün olmadı');
    }
  };
  
  const handleUpdateTariff = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/contract/tariffs/${selectedTariff.id}`, selectedTariff, { headers });
      toast.success('Tarif planı yeniləndi');
      setShowEditTariff(false);
      fetchData();
    } catch (error) {
      toast.error('Tarif planını yeniləmək mümkün olmadı');
    }
  };
  
  const handleDeleteTariff = async (id) => {
    if (!window.confirm('Bu tarif planını silmək istəyirsiniz?')) return;
    try {
      await axios.delete(`${API}/contract/tariffs/${id}`, { headers });
      toast.success('Tarif planı silindi');
      fetchData();
    } catch (error) {
      toast.error('Tarif planını silmək mümkün olmadı');
    }
  };
  
  const handleCreateEquipment = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/contract/equipment?category=${newEquipment.category}&name=${encodeURIComponent(newEquipment.name)}&specs=${encodeURIComponent(newEquipment.specs)}&price_azn=${newEquipment.price_azn}`, {}, { headers });
      toast.success('Avadanlıq seçimi yaradıldı');
      setShowCreateEquipment(false);
      setNewEquipment({ category: 'ont_router', name: '', specs: '', price_azn: 0 });
      fetchData();
    } catch (error) {
      toast.error('Avadanlıq seçimi yaratmaq mümkün olmadı');
    }
  };
  
  const handleDeleteEquipment = async (id) => {
    if (!window.confirm('Bu avadanlıq seçimini silmək istəyirsiniz?')) return;
    try {
      await axios.delete(`${API}/contract/equipment/${id}`, { headers });
      toast.success('Avadanlıq seçimi silindi');
      fetchData();
    } catch (error) {
      toast.error('Avadanlıq seçimini silmək mümkün olmadı');
    }
  };
  
  const handleUpdateEquipment = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/contract/equipment/${selectedEquipment.id}?name=${encodeURIComponent(selectedEquipment.name)}&specs=${encodeURIComponent(selectedEquipment.specs)}&price_azn=${selectedEquipment.price_azn}`, {}, { headers });
      toast.success('Avadanlıq seçimi yeniləndi');
      setShowEditEquipment(false);
      setSelectedEquipment(null);
      fetchData();
    } catch (error) {
      toast.error('Avadanlıq seçimini yeniləmək mümkün olmadı');
    }
  };
  
  const handleSeedDefaults = async () => {
    try {
      const res = await axios.post(`${API}/contract/seed-defaults`, {}, { headers });
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error('Default məlumatları yaratmaq mümkün olmadı');
    }
  };
  
  // Problem Types handlers
  const handleCreateProblemType = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/problem-types`, newProblemType, { headers });
      toast.success('Problem növü yaradıldı');
      setShowCreateProblemType(false);
      setNewProblemType({ category: 'internet', code: '', name: '', description: '', order: 0 });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Problem növü yaratmaq mümkün olmadı');
    }
  };
  
  const handleUpdateProblemType = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/problem-types/${selectedProblemType.id}`, {
        name: selectedProblemType.name,
        description: selectedProblemType.description,
        is_active: selectedProblemType.is_active,
        order: selectedProblemType.order
      }, { headers });
      toast.success('Problem növü yeniləndi');
      setShowEditProblemType(false);
      setSelectedProblemType(null);
      fetchData();
    } catch (error) {
      toast.error('Problem növünü yeniləmək mümkün olmadı');
    }
  };
  
  const handleDeleteProblemType = async (id) => {
    if (!window.confirm('Bu problem növünü silmək istəyirsiniz?')) return;
    try {
      await axios.delete(`${API}/problem-types/${id}`, { headers });
      toast.success('Problem növü silindi');
      fetchData();
    } catch (error) {
      toast.error('Problem növünü silmək mümkün olmadı');
    }
  };
  
  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/superadmin/create-admin`, newAdmin, { headers });
      toast.success('Admin yaradıldı');
      setShowCreateAdmin(false);
      setNewAdmin({ username: '', password: '', full_name: '', email: '', role: 'admin' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Admin yaratmaq mümkün olmadı');
    }
  };
  
  const handleDeleteAdmin = async (userId, username) => {
    if (!window.confirm(`${username} silinsin?`)) return;
    try {
      await axios.delete(`${API}/superadmin/delete-admin/${userId}`, { headers });
      toast.success('Admin silindi');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Silmək mümkün olmadı');
    }
  };
  
  const handleResetPassword = async () => {
    try {
      await axios.post(`${API}/superadmin/reset-password`, {
        user_id: selectedUser.id,
        new_password: newPassword
      }, { headers });
      toast.success('Şifrə sıfırlandı');
      setShowResetPassword(false);
      setNewPassword('');
      setSelectedUser(null);
    } catch (error) {
      toast.error('Şifrə sıfırlamaq mümkün olmadı');
    }
  };
  
  const handleToggleActive = async (userId) => {
    try {
      const res = await axios.put(`${API}/superadmin/user/${userId}/toggle-active`, {}, { headers });
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error('Status dəyişmək mümkün olmadı');
    }
  };
  
  // User Management Handlers (for Users tab)
  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/auth/register`, newUser, { headers });
      toast.success('İstifadəçi yaradıldı');
      setShowCreateUserDialog(false);
      setNewUser({ username: '', password: '', full_name: '', phone: '', email: '', role: 'operator', modules: [] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'İstifadəçi yaratmaq mümkün olmadı');
    }
  };
  
  const handleEditUser = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    try {
      await axios.put(`${API}/admin/users/${selectedUser.id}`, editUser, { headers });
      toast.success('İstifadəçi məlumatları yeniləndi');
      setShowEditUserDialog(false);
      setSelectedUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Yeniləmək mümkün olmadı');
    }
  };
  
  const handleUserPasswordChange = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    try {
      await axios.post(`${API}/admin/users/${selectedUser.id}/change-password`, { new_password: userNewPassword }, { headers });
      toast.success('Şifrə dəyişdirildi');
      setShowPasswordDialog(false);
      setUserNewPassword('');
      setSelectedUser(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Şifrə dəyişmək mümkün olmadı');
    }
  };
  
  const handleDeleteUser = async (userId, username) => {
    if (!window.confirm(`${username} silinsin?`)) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { headers });
      toast.success('İstifadəçi silindi');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Silmək mümkün olmadı');
    }
  };
  
  const openEditUserDialog = (user) => {
    setSelectedUser(user);
    setEditUser({
      full_name: user.full_name || '',
      phone: user.phone || '',
      email: user.email || '',
      modules: user.modules || []
    });
    setShowEditUserDialog(true);
  };
  
  const openPasswordDialog = (user) => {
    setSelectedUser(user);
    setUserNewPassword('');
    setShowPasswordDialog(true);
  };
  
  const handleUpdateSettings = async () => {
    try {
      await axios.put(`${API}/superadmin/settings`, settings, { headers });
      toast.success('Parametrlər yeniləndi');
    } catch (error) {
      toast.error('Parametrləri yeniləmək mümkün olmadı');
    }
  };
  
  const handleExport = async (type) => {
    try {
      const res = await axios.get(`${API}/superadmin/export/${type}`, { headers });
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}_export_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      toast.success(`${type} export edildi`);
    } catch (error) {
      toast.error('Export mümkün olmadı');
    }
  };
  
  // Tab Groups for better organization
  const tabGroups = [
    {
      id: 'main',
      label: 'Əsas',
      icon: BarChart3,
      tabs: [
        { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
        { id: 'map', label: 'Xəritə', icon: MapPin },
        { id: 'hr', label: 'HR', icon: Clock },
        { id: 'zones', label: 'İş Əraziləri', icon: MapPin },
        { id: 'broadcast', label: 'Bildirişlər', icon: Bell },
        { id: 'statistics', label: 'Statistika', icon: Trophy },
      ]
    },
    {
      id: 'management',
      label: 'İdarəetmə',
      icon: Users,
      tabs: [
        { id: 'groups', label: 'Qruplar', icon: MapPin },
        { id: 'admins', label: 'Adminlər', icon: Shield },
        { id: 'users', label: 'İstifadəçilər', icon: Users },
        { id: 'module_access', label: 'Modul İcazələri', icon: Shield },
      ]
    },
    {
      id: 'templates',
      label: 'Şablonlar',
      icon: FileText,
      tabs: [
        { id: 'contract', label: 'Müqavilə Şablonu', icon: FileText },
        { id: 'problemTypes', label: 'Problem Növləri', icon: ListChecks },
      ]
    },
    {
      id: 'system',
      label: 'Sistem',
      icon: Settings,
      tabs: [
        { id: 'audit', label: 'Audit Log', icon: Activity },
        { id: 'settings', label: 'Parametrlər', icon: Settings },
        { id: 'app_version', label: 'Mobil Versiya', icon: Smartphone },
        { id: 'task_stages', label: 'Tapşırıq Etapları', icon: ListChecks },
        { id: 'recurring_tasks', label: 'Periodik Tapşırıqlar', icon: ListChecks },
        { id: 'abills', label: 'ABillS', icon: Building2 },
        { id: 'export', label: 'Export', icon: Download },
      ]
    },
    {
      id: 'finance',
      label: 'Maliyyə',
      icon: Wallet,
      tabs: [
        { id: 'accounting', label: 'Mühasibatlıq', icon: Wallet },
      ]
    },
    {
      id: 'work',
      label: 'İşlər',
      icon: Briefcase,
      tabs: [
        { id: 'projects', label: 'Layihələr', icon: Briefcase },
        { id: 'tasks', label: 'Tapşırıqlar', icon: ListChecks },
      ]
    },
    {
      id: 'network',
      label: 'Şəbəkə',
      icon: Wifi,
      tabs: [
        { id: 'online', label: 'Online', icon: Wifi },
        { id: 'online_config', label: 'Online Konfiq', icon: Settings },
      ]
    },
  ];
  
  // Helper to find current tab's group
  const getCurrentGroup = () => {
    for (const group of tabGroups) {
      if (group.tabs.find(t => t.id === activeTab)) {
        return group;
      }
    }
    return tabGroups[0];
  };
  
  const getActionLabel = (action) => {
    const labels = {
      login: 'Giriş',
      logout: 'Çıxış',
      create_ticket: 'Ticket yaratdı',
      update_ticket: 'Ticket yenilədi',
      create_admin: 'Admin yaratdı',
      delete_admin: 'Admin sildi',
      reset_password: 'Şifrə sıfırladı',
      update_settings: 'Parametrləri yenilədi',
      activate_user: 'İstifadəçini aktivləşdirdi',
      deactivate_user: 'İstifadəçini deaktiv etdi',
    };
    return labels[action] || action;
  };
  
  return (
    <div className="min-h-screen bg-zinc-100">
      {/* Header */}
      <header className="bg-gradient-to-r from-purple-700 to-indigo-800 text-white p-4 shadow-lg">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Shield className="h-8 w-8" />
            <div>
              <h1 className="text-xl font-bold">Super Admin Panel</h1>
              <p className="text-sm text-purple-200">Sistem İdarəetməsi</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <TopBarTasksButton onOpenTasksPage={() => setActiveTab('tasks')} />
            <NotificationBell type="broadcast" />
            <Button 
              variant="ghost" 
              className="text-white hover:bg-white/20"
              onClick={onLogout}
            >
              Çıxış
            </Button>
          </div>
        </div>
      </header>
      
      {/* Tab Groups Navigation */}
      <div className="bg-white border-b shadow-sm">
        <div className="container mx-auto px-2">
          <div className="flex items-center gap-2 py-2 overflow-x-auto">
            {tabGroups.map(group => {
              const isActiveGroup = group.tabs.find(t => t.id === activeTab);
              const activeTabInGroup = group.tabs.find(t => t.id === activeTab);
              
              return (
                <DropdownMenu key={group.id}>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant={isActiveGroup ? 'default' : 'outline'}
                      size="sm"
                      className={`flex items-center gap-2 shrink-0 ${isActiveGroup ? 'bg-purple-600 hover:bg-purple-700' : 'hover:bg-zinc-100'}`}
                      data-testid={`tab-group-${group.id}`}
                    >
                      <group.icon className="h-4 w-4" />
                      <span>{group.label}</span>
                      {activeTabInGroup && (
                        <span className="text-xs opacity-75">({activeTabInGroup.label})</span>
                      )}
                      <ChevronDown className="h-3 w-3 ml-1" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="min-w-[180px]">
                    {group.tabs.map(tab => (
                      <DropdownMenuItem
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center gap-2 cursor-pointer ${activeTab === tab.id ? 'bg-purple-50 text-purple-700 font-medium' : ''}`}
                        data-testid={`tab-${tab.id}`}
                      >
                        <tab.icon className="h-4 w-4" />
                        {tab.label}
                        {activeTab === tab.id && (
                          <span className="ml-auto text-purple-600">✓</span>
                        )}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            })}
          </div>
        </div>
      </div>
      
      {/* Content */}
      <main className="container mx-auto p-6">
        {loading ? (
          <div className="flex justify-center py-12">
            <RefreshCw className="h-8 w-8 animate-spin text-purple-600" />
          </div>
        ) : (
          <>
            {/* Dashboard Tab */}
            {activeTab === 'dashboard' && stats && (
              <div className="space-y-4">
                {/* Row 1: Main Stats */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-blue-100">Ümumi İstifadəçi</p>
                          <p className="text-3xl font-bold">{stats.total_users}</p>
                        </div>
                        <Users className="h-10 w-10 text-blue-200" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-purple-100">Adminlər</p>
                          <p className="text-3xl font-bold">{stats.total_admins}</p>
                        </div>
                        <Shield className="h-10 w-10 text-purple-200" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-amber-100">Ümumi Ticket</p>
                          <p className="text-3xl font-bold">{stats.total_tickets}</p>
                        </div>
                        <Ticket className="h-10 w-10 text-amber-200" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-emerald-100">Qruplar</p>
                          <p className="text-3xl font-bold">{stats.total_groups}</p>
                        </div>
                        <Building2 className="h-10 w-10 text-emerald-200" />
                      </div>
                    </CardContent>
                  </Card>
                </div>
                
                {/* Row 2: Connection Stats */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-green-100">Ümumi Qoşulmalar</p>
                          <p className="text-3xl font-bold">{stats.total_connections || 0}</p>
                        </div>
                        <Wifi className="h-10 w-10 text-green-200" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-gradient-to-br from-yellow-500 to-yellow-600 text-white">
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-yellow-100">Gözləyən Qoşulmalar</p>
                          <p className="text-3xl font-bold">{stats.pending_connections || 0}</p>
                        </div>
                        <Clock className="h-10 w-10 text-yellow-200" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-gradient-to-br from-cyan-500 to-cyan-600 text-white">
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-cyan-100">İşdə Qoşulmalar</p>
                          <p className="text-3xl font-bold">{(stats.assigned_connections || 0) + (stats.in_progress_connections || 0)}</p>
                        </div>
                        <Wrench className="h-10 w-10 text-cyan-200" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-gradient-to-br from-teal-500 to-teal-600 text-white">
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-teal-100">Tamamlanmış</p>
                          <p className="text-3xl font-bold">{(stats.completed_connections || 0) + (stats.closed_connections || 0)}</p>
                        </div>
                        <Cable className="h-10 w-10 text-teal-200" />
                      </div>
                    </CardContent>
                  </Card>
                </div>
                
                {/* Row 3: Activity & Distribution */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Clock className="h-5 w-5" />
                        Son Fəaliyyət
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="text-center p-4 bg-zinc-50 rounded-lg">
                          <p className="text-3xl font-bold text-purple-600">{stats.recent_logins_24h}</p>
                          <p className="text-sm text-zinc-500">Giriş (24 saat)</p>
                        </div>
                        <div className="text-center p-4 bg-zinc-50 rounded-lg">
                          <p className="text-3xl font-bold text-amber-600">{stats.recent_tickets_7d}</p>
                          <p className="text-sm text-zinc-500">Yeni Ticket (7 gün)</p>
                        </div>
                        <div className="text-center p-4 bg-green-50 rounded-lg">
                          <p className="text-3xl font-bold text-green-600">{stats.recent_connections_7d || 0}</p>
                          <p className="text-sm text-zinc-500">Yeni Qoşulma (7 gün)</p>
                        </div>
                        <div className="text-center p-4 bg-teal-50 rounded-lg">
                          <p className="text-3xl font-bold text-teal-600">{stats.completed_connections_7d || 0}</p>
                          <p className="text-sm text-zinc-500">Tamamlanmış (7 gün)</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader>
                      <CardTitle>İstifadəçi Bölgüsü</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span>Operatorlar</span>
                          <span className="font-bold">{stats.total_operators}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span>Texniklər</span>
                          <span className="font-bold">{stats.total_technicians}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span>Adminlər</span>
                          <span className="font-bold">{stats.total_admins}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
                
                {/* Row 4: Connection Status Distribution */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Wifi className="h-5 w-5" />
                      Qoşulma Status Bölgüsü
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      <div className="text-center p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                        <p className="text-2xl font-bold text-yellow-600">{stats.pending_connections || 0}</p>
                        <p className="text-sm text-yellow-800">Gözləyən</p>
                      </div>
                      <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <p className="text-2xl font-bold text-blue-600">{stats.assigned_connections || 0}</p>
                        <p className="text-sm text-blue-800">Təyin olunmuş</p>
                      </div>
                      <div className="text-center p-4 bg-purple-50 rounded-lg border border-purple-200">
                        <p className="text-2xl font-bold text-purple-600">{stats.in_progress_connections || 0}</p>
                        <p className="text-sm text-purple-800">İşdə</p>
                      </div>
                      <div className="text-center p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                        <p className="text-2xl font-bold text-emerald-600">{stats.completed_connections || 0}</p>
                        <p className="text-sm text-emerald-800">Tamamlanmış</p>
                      </div>
                      <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
                        <p className="text-2xl font-bold text-green-600">{stats.closed_connections || 0}</p>
                        <p className="text-sm text-green-800">Bağlanmış</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Row 5: D2D Statistics */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="h-5 w-5 text-orange-600" />
                      D2D Satış Statistikası
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center p-4 bg-orange-50 rounded-lg border border-orange-200">
                        <p className="text-2xl font-bold text-orange-600">{stats.d2d_total || 0}</p>
                        <p className="text-sm text-orange-800">Ümumi Qeydlər</p>
                      </div>
                      <div className="text-center p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                        <p className="text-2xl font-bold text-yellow-600">{stats.d2d_new || 0}</p>
                        <p className="text-sm text-yellow-800">Yeni</p>
                      </div>
                      <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
                        <p className="text-2xl font-bold text-green-600">{stats.d2d_converted || 0}</p>
                        <p className="text-sm text-green-800">Çevrilmiş</p>
                      </div>
                      <div className="text-center p-4 bg-purple-50 rounded-lg border border-purple-200">
                        <p className="text-2xl font-bold text-purple-600">{stats.d2d_conversion_rate || 0}%</p>
                        <p className="text-sm text-purple-800">Çevrilmə Nisbəti</p>
                      </div>
                    </div>
                    {stats.d2d_recent_7d > 0 && (
                      <div className="mt-4 text-center p-3 bg-orange-100 rounded-lg">
                        <p className="text-sm text-orange-800">
                          Son 7 gündə <span className="font-bold">{stats.d2d_recent_7d}</span> yeni D2D qeydi
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
            
            {/* Groups Tab */}
            {activeTab === 'groups' && (
              <GroupManagement token={token} />
            )}
            
            {/* Map Tab */}
            {activeTab === 'map' && (
              <MapPanel user={user} />
            )}

            {/* HR Tab */}
            {activeTab === 'hr' && (
              <HRPanel user={user} />
            )}

            {activeTab === 'accounting' && (
              <AccountingPanel user={user} />
            )}

            {/* Projects Tab */}
            {activeTab === 'projects' && (
              <ProjectsPanel user={user} />
            )}

            {/* Tasks Tab — Standalone Tasks Module (Sprint 10-11) */}
            {activeTab === 'tasks' && (
              <TasksPanel user={user} />
            )}

            {/* Tapşırıq Etapları — Stage Management (Sprint A) */}
            {activeTab === 'task_stages' && (
              <StageManagementPanel />
            )}

            {/* Periodik Tapşırıqlar — Recurring Rules (Sprint B) */}
            {activeTab === 'recurring_tasks' && (
              <RecurringTasksPanel />
            )}

            {/* Online — Zabbix Live Network Metrics */}
            {activeTab === 'online' && (
              <OnlinePanel user={user} />
            )}

            {/* Online Config — Zabbix VLAN <-> Group mapping (SuperAdmin only) */}
            {activeTab === 'online_config' && (
              <OnlineConfigPanel />
            )}

            {/* Module Access Manager Tab */}
            {activeTab === 'module_access' && (
              <ModuleAccessPanel />
            )}

            {/* Work Zones Tab */}
            {activeTab === 'zones' && (
              <WorkZonesPanel />
            )}

            {/* App Version (Force Update) Tab */}
            {activeTab === 'app_version' && (
              <AppVersionPanel />
            )}

            {/* Broadcast Tab */}
            {activeTab === 'broadcast' && (
              <BroadcastPanel user={user} />
            )}

            {/* Statistics Tab */}
            {activeTab === 'statistics' && (
              <div className="space-y-6">
                {/* Report Filters */}
                <StatisticsFilters
                  groups={allGroups}
                  selectedGroupIds={selectedGroupIds}
                  setSelectedGroupIds={setSelectedGroupIds}
                  period={period}
                  setPeriod={setPeriod}
                  startDate={startDate}
                  setStartDate={setStartDate}
                  endDate={endDate}
                  setEndDate={setEndDate}
                  displayDateRange=""
                  showGroupFilter={true}
                />
                
                {/* Services/Connections/D2D Sub-tabs */}
                <div className="flex gap-2 flex-wrap">
                  <Button
                    onClick={() => setStatsSubTab('services')}
                    className={statsSubTab === 'services' ? 'bg-blue-600 text-white' : 'bg-white text-zinc-900 hover:bg-zinc-100 border'}
                  >
                    <Wrench className="h-4 w-4 mr-2" />
                    Servislər
                  </Button>
                  <Button
                    onClick={() => setStatsSubTab('connections')}
                    className={statsSubTab === 'connections' ? 'bg-green-600 text-white' : 'bg-white text-zinc-900 hover:bg-zinc-100 border'}
                  >
                    <Phone className="h-4 w-4 mr-2" />
                    Qoşulmalar
                  </Button>
                  <Button
                    onClick={() => setStatsSubTab('d2d')}
                    className={statsSubTab === 'd2d' ? 'bg-orange-600 text-white' : 'bg-white text-zinc-900 hover:bg-zinc-100 border'}
                  >
                    <Users className="h-4 w-4 mr-2" />
                    D2D Satış
                  </Button>
                  <Button
                    onClick={() => setStatsSubTab('performance')}
                    className={statsSubTab === 'performance' ? 'bg-purple-600 text-white' : 'bg-white text-zinc-900 hover:bg-zinc-100 border'}
                    data-testid="subtab-performance"
                  >
                    <Trophy className="h-4 w-4 mr-2" />
                    Texnik Performans
                  </Button>
                  <Button
                    onClick={() => setStatsSubTab('map')}
                    className={statsSubTab === 'map' ? 'bg-teal-600 text-white' : 'bg-white text-zinc-900 hover:bg-zinc-100 border'}
                    data-testid="subtab-map"
                  >
                    <MapPin className="h-4 w-4 mr-2" />
                    Xəritə
                  </Button>
                </div>
                
                {/* Services Statistics */}
                {statsSubTab === 'services' && statistics && (
                  <div className="space-y-6">
                    <ServiceStatsCards statistics={statistics} />
                    <ServiceStatusBreakdown statistics={statistics} />
                    <TechnicianLeaderboard scores={technicianScores} />
                    <OperatorLeaderboard scores={operatorScores} />
                    <ScoringRules />
                  </div>
                )}
                
                {/* Connections Statistics */}
                {statsSubTab === 'connections' && connectionStats && (
                  <div className="space-y-6">
                    <ConnectionStatsCards connectionStats={connectionStats} />
                    <ConnectionStatusBreakdown connectionStats={connectionStats} />
                  </div>
                )}
                
                {/* D2D Statistics */}
                {statsSubTab === 'd2d' && d2dStats && (
                  <div className="space-y-6">
                    <D2DStatsCards d2dStats={d2dStats} />
                    
                    {/* D2D Status Breakdown */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Users className="h-5 w-5 text-orange-600" />
                          D2D Status Bölgüsü
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className="text-center p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                            <p className="text-2xl font-bold text-yellow-600">{d2dStats.new || 0}</p>
                            <p className="text-sm text-yellow-800">Yeni</p>
                          </div>
                          <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
                            <p className="text-2xl font-bold text-blue-600">{d2dStats.contacted || 0}</p>
                            <p className="text-sm text-blue-800">Əlaqə alınıb</p>
                          </div>
                          <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
                            <p className="text-2xl font-bold text-green-600">{d2dStats.converted || 0}</p>
                            <p className="text-sm text-green-800">Çevrilmiş</p>
                          </div>
                          <div className="text-center p-4 bg-red-50 rounded-lg border border-red-200">
                            <p className="text-2xl font-bold text-red-600">{d2dStats.cancelled || 0}</p>
                            <p className="text-sm text-red-800">Ləğv edilmiş</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    
                    <D2DTopAgents d2dStats={d2dStats} />
                  </div>
                )}

                {/* Texnik Performans */}
                {statsSubTab === 'performance' && (
                  <TechnicianPerformancePanel user={user} />
                )}

                {/* Xəritə */}
                {statsSubTab === 'map' && (
                  <MapPanel user={user} />
                )}
              </div>
            )}
            
            {/* Admins Tab */}
            {activeTab === 'admins' && (
              <AdminManagementPanel
                admins={admins}
                onCreateAdmin={() => setShowCreateAdmin(true)}
                onResetPassword={(admin) => { setSelectedUser(admin); setShowResetPassword(true); }}
                onDeleteAdmin={handleDeleteAdmin}
              />
            )}
            
            {/* Users Tab */}
            {activeTab === 'users' && (
              <UserManagementPanel
                users={users}
                currentUserId={user?.id}
                showCreateUserDialog={showCreateUserDialog}
                setShowCreateUserDialog={setShowCreateUserDialog}
                newUser={newUser}
                setNewUser={setNewUser}
                onCreateUser={handleCreateUser}
                showEditUserDialog={showEditUserDialog}
                setShowEditUserDialog={setShowEditUserDialog}
                selectedUser={selectedUser}
                editUser={editUser}
                setEditUser={setEditUser}
                onEditUser={handleEditUser}
                onOpenEditDialog={openEditUserDialog}
                showPasswordDialog={showPasswordDialog}
                setShowPasswordDialog={setShowPasswordDialog}
                newPassword={userNewPassword}
                setNewPassword={setUserNewPassword}
                onChangePassword={handleUserPasswordChange}
                onOpenPasswordDialog={openPasswordDialog}
                onDeleteUser={handleDeleteUser}
                availableRoles={['admin', 'operator', 'technician', 'd2d_agent', 'accountant', 'lawyer', 'network_admin', 'system_admin']}
              />
            )}
            
            {/* Audit Log Tab */}
            {activeTab === 'audit' && (
              <AuditLogsPanel auditLogs={auditLogs} />
            )}
            
            {/* Settings Tab */}
            {activeTab === 'settings' && settings && (
              <SettingsPanel
                settings={settings}
                setSettings={setSettings}
                onSave={handleUpdateSettings}
              />
            )}
            
            {/* ABillS Tab */}
            {activeTab === 'abills' && (
              <div className="space-y-4">
                <h2 className="text-xl font-bold">ABillS İnteqrasiyası</h2>
                
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Building2 className="h-5 w-5" />
                      Bağlantı Konfiqurasiyası
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>ABillS Server URL</Label>
                        <Input
                          placeholder="https://billing.company.az"
                          value={abillsConfig.server_url}
                          onChange={(e) => setAbillsConfig({...abillsConfig, server_url: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>API Key</Label>
                        <Input
                          type="password"
                          placeholder="API açarını daxil edin"
                          value={abillsConfig.api_key}
                          onChange={(e) => setAbillsConfig({...abillsConfig, api_key: e.target.value})}
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <Button className="bg-purple-600 hover:bg-purple-700" disabled>
                        Bağlantını Yoxla
                      </Button>
                      <span className={`px-3 py-1 rounded text-sm ${abillsConfig.connected ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {abillsConfig.connected ? '✓ Bağlı' : '○ Bağlı deyil'}
                      </span>
                    </div>
                    <p className="text-sm text-zinc-500">
                      ABillS inteqrasiyası üçün server URL və API key tələb olunur. 
                      <br />API Key almaq üçün: ABillS Admin Panel → Настройки → Администраторы → API_KEY
                    </p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader>
                    <CardTitle>Sinxronizasiya Seçimləri</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-zinc-600">ABillS-dən aşağıdakı məlumatları çəkmək mümkün olacaq:</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="border rounded p-4 text-center bg-zinc-50">
                        <Users className="h-8 w-8 mx-auto mb-2 text-blue-500" />
                        <div className="font-medium">Abunəçilər</div>
                        <div className="text-xs text-zinc-500">Ad, telefon, ünvan</div>
                      </div>
                      <div className="border rounded p-4 text-center bg-zinc-50">
                        <Wifi className="h-8 w-8 mx-auto mb-2 text-green-500" />
                        <div className="font-medium">Tarif Planları</div>
                        <div className="text-xs text-zinc-500">Ad, sürət, qiymət</div>
                      </div>
                      <div className="border rounded p-4 text-center bg-zinc-50">
                        <MapPin className="h-8 w-8 mx-auto mb-2 text-orange-500" />
                        <div className="font-medium">Qruplar/Ünvanlar</div>
                        <div className="text-xs text-zinc-500">Rayonlar, küçələr</div>
                      </div>
                      <div className="border rounded p-4 text-center bg-zinc-50">
                        <Cable className="h-8 w-8 mx-auto mb-2 text-purple-500" />
                        <div className="font-medium">PON Məlumatları</div>
                        <div className="text-xs text-zinc-500">ONU, RX/TX power</div>
                      </div>
                    </div>
                    <div className="bg-amber-50 border border-amber-200 rounded p-4">
                      <p className="text-amber-800 text-sm">
                        <strong>Qeyd:</strong> İnteqrasiya üçün əvvəlcə ABillS server URL və API Key daxil edin. 
                        Bağlantı qurulduqdan sonra sinxronizasiya funksiyaları aktiv olacaq.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
            
            {/* Export Tab */}
            {activeTab === 'export' && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => handleExport('tickets')}>
                  <CardContent className="p-6 text-center">
                    <Ticket className="h-12 w-12 mx-auto mb-4 text-amber-500" />
                    <h3 className="font-bold">Tiketlər</h3>
                    <p className="text-sm text-zinc-500">Bütün tiketləri JSON formatında export et</p>
                  </CardContent>
                </Card>
                
                <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => handleExport('users')}>
                  <CardContent className="p-6 text-center">
                    <Users className="h-12 w-12 mx-auto mb-4 text-blue-500" />
                    <h3 className="font-bold">İstifadəçilər</h3>
                    <p className="text-sm text-zinc-500">Bütün istifadəçiləri export et</p>
                  </CardContent>
                </Card>
                
                <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => handleExport('audit-logs')}>
                  <CardContent className="p-6 text-center">
                    <Activity className="h-12 w-12 mx-auto mb-4 text-purple-500" />
                    <h3 className="font-bold">Audit Loglar</h3>
                    <p className="text-sm text-zinc-500">Bütün sistem loglarını export et</p>
                  </CardContent>
                </Card>
              </div>
            )}
            
            {/* Contract Template Tab */}
            {activeTab === 'contract' && (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold">Müqavilə Şablonu Redaktoru</h2>
                  <Button onClick={handleSeedDefaults} variant="outline">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Default Məlumatları Yarat
                  </Button>
                </div>
                
                {/* Sub-tabs */}
                <div className="flex gap-2 mb-4 flex-wrap">
                  {[
                    { id: 'company', label: 'Şirkət Məlumatları' },
                    { id: 'fees', label: 'Qiymətlər' },
                    { id: 'tariffs', label: 'Tarif Planları' },
                    { id: 'equipment', label: 'Avadanlıq' },
                    { id: 'terms', label: 'Şərtlər' },
                  ].map(sub => (
                    <Button
                      key={sub.id}
                      variant={contractSection === sub.id ? 'default' : 'outline'}
                      onClick={() => setContractSection(sub.id)}
                      className={contractSection === sub.id ? 'bg-purple-600' : ''}
                    >
                      {sub.label}
                    </Button>
                  ))}
                </div>
                
                {/* Company Info Section */}
                {contractSection === 'company' && contractTemplate && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Şirkət Məlumatları</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label>Şirkət adı</Label>
                          <Input
                            value={contractTemplate.company_name || ''}
                            onChange={(e) => setContractTemplate({...contractTemplate, company_name: e.target.value})}
                            onBlur={(e) => handleUpdateContractTemplate('company_name', e.target.value)}
                          />
                        </div>
                        <div>
                          <Label>VÖEN</Label>
                          <Input
                            value={contractTemplate.company_voen || ''}
                            onChange={(e) => setContractTemplate({...contractTemplate, company_voen: e.target.value})}
                            onBlur={(e) => handleUpdateContractTemplate('company_voen', e.target.value)}
                          />
                        </div>
                        <div>
                          <Label>Telefon</Label>
                          <Input
                            value={contractTemplate.company_phone || ''}
                            onChange={(e) => setContractTemplate({...contractTemplate, company_phone: e.target.value})}
                            onBlur={(e) => handleUpdateContractTemplate('company_phone', e.target.value)}
                          />
                        </div>
                        <div>
                          <Label>Email</Label>
                          <Input
                            value={contractTemplate.company_email || ''}
                            onChange={(e) => setContractTemplate({...contractTemplate, company_email: e.target.value})}
                            onBlur={(e) => handleUpdateContractTemplate('company_email', e.target.value)}
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Ünvan</Label>
                        <Textarea
                          value={contractTemplate.company_address || ''}
                          onChange={(e) => setContractTemplate({...contractTemplate, company_address: e.target.value})}
                          onBlur={(e) => handleUpdateContractTemplate('company_address', e.target.value)}
                          rows={2}
                        />
                      </div>
                      <div>
                        <Label>Logo</Label>
                        <div className="flex items-center gap-4 mt-2">
                          {contractTemplate.company_logo_url && (
                            <img src={contractTemplate.company_logo_url} alt="Logo" className="h-16 w-auto border rounded" />
                          )}
                          <label className="cursor-pointer">
                            <Button variant="outline" asChild>
                              <span>
                                <Upload className="h-4 w-4 mr-2" />
                                Logo Yüklə
                              </span>
                            </Button>
                            <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
                          </label>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {/* Fees Section */}
                {contractSection === 'fees' && contractTemplate && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Xidmət Qiymətləri</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label>Qoşulma haqqı (AZN)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={contractTemplate.connection_fee || 0}
                            onChange={(e) => setContractTemplate({...contractTemplate, connection_fee: parseFloat(e.target.value)})}
                            onBlur={(e) => handleUpdateContractTemplate('connection_fee', parseFloat(e.target.value))}
                          />
                        </div>
                        <div>
                          <Label>Bərpa haqqı (AZN)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={contractTemplate.restoration_fee || 0}
                            onChange={(e) => setContractTemplate({...contractTemplate, restoration_fee: parseFloat(e.target.value)})}
                            onBlur={(e) => handleUpdateContractTemplate('restoration_fee', parseFloat(e.target.value))}
                          />
                        </div>
                        <div>
                          <Label>Texniki çağırış haqqı (AZN)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={contractTemplate.technician_call_fee || 0}
                            onChange={(e) => setContractTemplate({...contractTemplate, technician_call_fee: parseFloat(e.target.value)})}
                            onBlur={(e) => handleUpdateContractTemplate('technician_call_fee', parseFloat(e.target.value))}
                          />
                        </div>
                        <div>
                          <Label>Blok xətti bərpası (AZN)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={contractTemplate.block_repair_fee || 0}
                            onChange={(e) => setContractTemplate({...contractTemplate, block_repair_fee: parseFloat(e.target.value)})}
                            onBlur={(e) => handleUpdateContractTemplate('block_repair_fee', parseFloat(e.target.value))}
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {/* Tariff Plans Section */}
                {contractSection === 'tariffs' && (
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between">
                      <CardTitle>Tarif Planları</CardTitle>
                      <Button onClick={() => setShowCreateTariff(true)} size="sm" className="bg-purple-600 hover:bg-purple-700">
                        <Plus className="h-4 w-4 mr-2" />
                        Yeni Tarif
                      </Button>
                    </CardHeader>
                    <CardContent>
                      <table className="w-full">
                        <thead className="bg-zinc-50">
                          <tr>
                            <th className="text-left p-3">Ad</th>
                            <th className="text-left p-3">Sürət</th>
                            <th className="text-left p-3">Qiymət</th>
                            <th className="text-left p-3">Status</th>
                            <th className="text-right p-3">Əməliyyatlar</th>
                          </tr>
                        </thead>
                        <tbody>
                          {tariffPlans.map(tariff => (
                            <tr key={tariff.id} className={`border-t ${!tariff.is_active ? 'opacity-50' : ''}`}>
                              <td className="p-3 font-medium">{tariff.name}</td>
                              <td className="p-3">{tariff.speed_mbps} Mbit/s</td>
                              <td className="p-3">{tariff.price_azn} AZN</td>
                              <td className="p-3">
                                <span className={`px-2 py-1 rounded text-xs ${tariff.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                  {tariff.is_active ? 'Aktiv' : 'Deaktiv'}
                                </span>
                              </td>
                              <td className="p-3 text-right">
                                <Button size="sm" variant="outline" className="mr-2" onClick={() => { setSelectedTariff(tariff); setShowEditTariff(true); }}>
                                  <Edit2 className="h-4 w-4" />
                                </Button>
                                <Button size="sm" variant="destructive" onClick={() => handleDeleteTariff(tariff.id)}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                          {tariffPlans.length === 0 && (
                            <tr><td colSpan="5" className="p-4 text-center text-zinc-500">Tarif planı yoxdur. &quot;Default Məlumatları Yarat&quot; düyməsinə basın.</td></tr>
                          )}
                        </tbody>
                      </table>
                    </CardContent>
                  </Card>
                )}
                
                {/* Equipment Section */}
                {contractSection === 'equipment' && (
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between">
                      <CardTitle>Avadanlıq Seçimləri</CardTitle>
                      <Button onClick={() => setShowCreateEquipment(true)} size="sm" className="bg-purple-600 hover:bg-purple-700">
                        <Plus className="h-4 w-4 mr-2" />
                        Yeni Avadanlıq
                      </Button>
                    </CardHeader>
                    <CardContent>
                      {EQUIPMENT_CATEGORIES.map(cat => (
                        <div key={cat.value} className="mb-6">
                          <h3 className="font-semibold mb-2 text-purple-700">{cat.label}</h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {equipmentOptions.filter(e => e.category === cat.value).map(eq => (
                              <div key={eq.id} className="border rounded p-3 hover:shadow-md transition-shadow">
                                <div className="flex justify-between items-start">
                                  <div className="flex-1">
                                    <div className="font-medium">{eq.name}</div>
                                    <div className="text-sm text-zinc-500">{eq.specs}</div>
                                    {eq.price_azn > 0 && <div className="text-sm font-medium text-green-600 mt-1">{eq.price_azn} AZN</div>}
                                  </div>
                                  <div className="flex gap-1">
                                    <Button size="sm" variant="ghost" onClick={() => { setSelectedEquipment(eq); setShowEditEquipment(true); }}>
                                      <Edit2 className="h-4 w-4 text-blue-500" />
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={() => handleDeleteEquipment(eq.id)}>
                                      <Trash2 className="h-4 w-4 text-red-500" />
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                          {equipmentOptions.filter(e => e.category === cat.value).length === 0 && (
                            <p className="text-zinc-500 text-sm">Bu kateqoriyada avadanlıq yoxdur</p>
                          )}
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}
                
                {/* Terms Section */}
                {contractSection === 'terms' && contractTemplate && (
                  <div className="space-y-4">
                    <Card>
                      <CardHeader><CardTitle>Müqavilənin Mövzusu</CardTitle></CardHeader>
                      <CardContent>
                        <Textarea
                          value={contractTemplate.contract_subject || ''}
                          onChange={(e) => setContractTemplate({...contractTemplate, contract_subject: e.target.value})}
                          onBlur={(e) => handleUpdateContractTemplate('contract_subject', e.target.value)}
                          rows={4}
                        />
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader><CardTitle>Ödəniş Şərtləri</CardTitle></CardHeader>
                      <CardContent>
                        <Textarea
                          value={contractTemplate.payment_terms || ''}
                          onChange={(e) => setContractTemplate({...contractTemplate, payment_terms: e.target.value})}
                          onBlur={(e) => handleUpdateContractTemplate('payment_terms', e.target.value)}
                          rows={4}
                        />
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader><CardTitle>Xidmət Şərtləri</CardTitle></CardHeader>
                      <CardContent>
                        <Textarea
                          value={contractTemplate.service_terms || ''}
                          onChange={(e) => setContractTemplate({...contractTemplate, service_terms: e.target.value})}
                          onBlur={(e) => handleUpdateContractTemplate('service_terms', e.target.value)}
                          rows={4}
                        />
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader><CardTitle>Fors Major</CardTitle></CardHeader>
                      <CardContent>
                        <Textarea
                          value={contractTemplate.force_majeure || ''}
                          onChange={(e) => setContractTemplate({...contractTemplate, force_majeure: e.target.value})}
                          onBlur={(e) => handleUpdateContractTemplate('force_majeure', e.target.value)}
                          rows={3}
                        />
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader><CardTitle>Mübahisələrin Həlli</CardTitle></CardHeader>
                      <CardContent>
                        <Textarea
                          value={contractTemplate.dispute_resolution || ''}
                          onChange={(e) => setContractTemplate({...contractTemplate, dispute_resolution: e.target.value})}
                          onBlur={(e) => handleUpdateContractTemplate('dispute_resolution', e.target.value)}
                          rows={3}
                        />
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader><CardTitle>Məxfilik</CardTitle></CardHeader>
                      <CardContent>
                        <Textarea
                          value={contractTemplate.confidentiality || ''}
                          onChange={(e) => setContractTemplate({...contractTemplate, confidentiality: e.target.value})}
                          onBlur={(e) => handleUpdateContractTemplate('confidentiality', e.target.value)}
                          rows={3}
                        />
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader><CardTitle>Yekun Müddəalar</CardTitle></CardHeader>
                      <CardContent>
                        <Textarea
                          value={contractTemplate.final_provisions || ''}
                          onChange={(e) => setContractTemplate({...contractTemplate, final_provisions: e.target.value})}
                          onBlur={(e) => handleUpdateContractTemplate('final_provisions', e.target.value)}
                          rows={3}
                        />
                      </CardContent>
                    </Card>
                  </div>
                )}
              </div>
            )}
            
            {/* Problem Types Tab */}
            {activeTab === 'problemTypes' && (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold">Problem Növləri İdarəetməsi</h2>
                  <Button onClick={() => setShowCreateProblemType(true)} className="bg-purple-600 hover:bg-purple-700">
                    <Plus className="h-4 w-4 mr-2" />
                    Yeni Problem Növü
                  </Button>
                </div>
                
                <div className="space-y-4">
                  {CATEGORIES.map(cat => (
                    <Card key={cat.value}>
                      <CardHeader 
                        className="cursor-pointer hover:bg-zinc-50"
                        onClick={() => setExpandedCategory(expandedCategory === cat.value ? '' : cat.value)}
                      >
                        <CardTitle className="flex items-center justify-between">
                          <span className="flex items-center gap-2">
                            <ListChecks className="h-5 w-5" />
                            {cat.label}
                            <span className="text-sm font-normal text-zinc-500">
                              ({(problemTypes[cat.value] || []).length} növ)
                            </span>
                          </span>
                          {expandedCategory === cat.value ? <ChevronUp /> : <ChevronDown />}
                        </CardTitle>
                      </CardHeader>
                      
                      {expandedCategory === cat.value && (
                        <CardContent className="pt-0">
                          <table className="w-full">
                            <thead className="bg-zinc-50">
                              <tr>
                                <th className="text-left p-3">Kod</th>
                                <th className="text-left p-3">Ad</th>
                                <th className="text-left p-3">Təsvir</th>
                                <th className="text-left p-3">Sıra</th>
                                <th className="text-left p-3">Status</th>
                                <th className="text-right p-3">Əməliyyatlar</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(problemTypes[cat.value] || []).map(pt => (
                                <tr key={pt.id} className={`border-t ${!pt.is_active ? 'opacity-50' : ''}`}>
                                  <td className="p-3 font-mono text-sm">{pt.code}</td>
                                  <td className="p-3 font-medium">{pt.name}</td>
                                  <td className="p-3 text-sm text-zinc-600">{pt.description || '-'}</td>
                                  <td className="p-3">{pt.order}</td>
                                  <td className="p-3">
                                    <span className={`px-2 py-1 rounded text-xs ${
                                      pt.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                    }`}>
                                      {pt.is_active ? 'Aktiv' : 'Deaktiv'}
                                    </span>
                                  </td>
                                  <td className="p-3 text-right">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="mr-2"
                                      onClick={() => { setSelectedProblemType(pt); setShowEditProblemType(true); }}
                                    >
                                      <Edit2 className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="destructive"
                                      onClick={() => handleDeleteProblemType(pt.id)}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                              {(problemTypes[cat.value] || []).length === 0 && (
                                <tr>
                                  <td colSpan="6" className="p-4 text-center text-zinc-500">
                                    Bu kateqoriya üçün problem növü yoxdur
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </CardContent>
                      )}
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </main>
      
      {/* Create Admin Dialog */}
      <Dialog open={showCreateAdmin} onOpenChange={setShowCreateAdmin}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Yeni Admin Yarat</DialogTitle>
            <DialogDescription>Yeni admin və ya super admin hesabı yaradın</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateAdmin} className="space-y-4">
            <div>
              <Label>İstifadəçi adı</Label>
              <Input
                value={newAdmin.username}
                onChange={(e) => setNewAdmin({ ...newAdmin, username: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Şifrə</Label>
              <Input
                type="password"
                value={newAdmin.password}
                onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Tam ad</Label>
              <Input
                value={newAdmin.full_name}
                onChange={(e) => setNewAdmin({ ...newAdmin, full_name: e.target.value })}
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={newAdmin.email}
                onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
              />
            </div>
            <div>
              <Label>Rol</Label>
              <Select value={newAdmin.role} onValueChange={(v) => setNewAdmin({ ...newAdmin, role: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="superadmin">Super Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" className="w-full bg-purple-600 hover:bg-purple-700">
              Yarat
            </Button>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Reset Password Dialog */}
      <Dialog open={showResetPassword} onOpenChange={setShowResetPassword}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Şifrə Sıfırla</DialogTitle>
            <DialogDescription>
              {selectedUser?.username} üçün yeni şifrə təyin edin
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Yeni Şifrə</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </div>
            <Button onClick={handleResetPassword} className="w-full bg-purple-600 hover:bg-purple-700">
              Şifrəni Sıfırla
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Create Problem Type Dialog */}
      <Dialog open={showCreateProblemType} onOpenChange={setShowCreateProblemType}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Yeni Problem Növü</DialogTitle>
            <DialogDescription>Operator panelində görünəcək yeni problem növü yaradın</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateProblemType} className="space-y-4">
            <div>
              <Label>Kateqoriya</Label>
              <Select value={newProblemType.category} onValueChange={(v) => setNewProblemType({ ...newProblemType, category: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Kod (unikal)</Label>
              <Input
                value={newProblemType.code}
                onChange={(e) => setNewProblemType({ ...newProblemType, code: e.target.value.toLowerCase().replace(/\s/g, '_') })}
                placeholder="məs: slow_internet"
                required
              />
            </div>
            <div>
              <Label>Ad (Azərbaycan dilində)</Label>
              <Input
                value={newProblemType.name}
                onChange={(e) => setNewProblemType({ ...newProblemType, name: e.target.value })}
                placeholder="məs: İnternet yavaşdır"
                required
              />
            </div>
            <div>
              <Label>Avtomatik doldurulan təsvir</Label>
              <Textarea
                value={newProblemType.description}
                onChange={(e) => setNewProblemType({ ...newProblemType, description: e.target.value })}
                placeholder="Operator seçdikdə avtomatik yazılacaq mətn"
                rows={3}
              />
            </div>
            <div>
              <Label>Sıra nömrəsi</Label>
              <Input
                type="number"
                value={newProblemType.order}
                onChange={(e) => setNewProblemType({ ...newProblemType, order: parseInt(e.target.value) || 0 })}
              />
            </div>
            <Button type="submit" className="w-full bg-purple-600 hover:bg-purple-700">
              Yarat
            </Button>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Edit Problem Type Dialog */}
      <Dialog open={showEditProblemType} onOpenChange={setShowEditProblemType}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Problem Növünü Redaktə Et</DialogTitle>
            <DialogDescription>
              {selectedProblemType?.code} - {selectedProblemType?.name}
            </DialogDescription>
          </DialogHeader>
          {selectedProblemType && (
            <form onSubmit={handleUpdateProblemType} className="space-y-4">
              <div>
                <Label>Ad</Label>
                <Input
                  value={selectedProblemType.name}
                  onChange={(e) => setSelectedProblemType({ ...selectedProblemType, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Avtomatik doldurulan təsvir</Label>
                <Textarea
                  value={selectedProblemType.description || ''}
                  onChange={(e) => setSelectedProblemType({ ...selectedProblemType, description: e.target.value })}
                  rows={3}
                />
              </div>
              <div>
                <Label>Sıra nömrəsi</Label>
                <Input
                  type="number"
                  value={selectedProblemType.order}
                  onChange={(e) => setSelectedProblemType({ ...selectedProblemType, order: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={selectedProblemType.is_active}
                  onChange={(e) => setSelectedProblemType({ ...selectedProblemType, is_active: e.target.checked })}
                  className="rounded"
                />
                <Label htmlFor="is_active">Aktiv</Label>
              </div>
              <Button type="submit" className="w-full bg-purple-600 hover:bg-purple-700">
                Yadda Saxla
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Create Tariff Dialog */}
      <Dialog open={showCreateTariff} onOpenChange={setShowCreateTariff}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Yeni Tarif Planı</DialogTitle>
            <DialogDescription>Abunəçilər üçün yeni tarif planı yaradın</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateTariff} className="space-y-4">
            <div>
              <Label>Tarif adı</Label>
              <Input
                value={newTariff.name}
                onChange={(e) => setNewTariff({ ...newTariff, name: e.target.value })}
                placeholder="məs: Premium 100"
                required
              />
            </div>
            <div>
              <Label>Sürət (Mbit/s)</Label>
              <Input
                type="number"
                value={newTariff.speed_mbps}
                onChange={(e) => setNewTariff({ ...newTariff, speed_mbps: parseInt(e.target.value) || 0 })}
                required
              />
            </div>
            <div>
              <Label>Qiymət (AZN)</Label>
              <Input
                type="number"
                step="0.01"
                value={newTariff.price_azn}
                onChange={(e) => setNewTariff({ ...newTariff, price_azn: parseFloat(e.target.value) || 0 })}
                required
              />
            </div>
            <div>
              <Label>Təsvir (opsional)</Label>
              <Input
                value={newTariff.description}
                onChange={(e) => setNewTariff({ ...newTariff, description: e.target.value })}
              />
            </div>
            <Button type="submit" className="w-full bg-purple-600 hover:bg-purple-700">
              Yarat
            </Button>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Edit Tariff Dialog */}
      <Dialog open={showEditTariff} onOpenChange={setShowEditTariff}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Tarif Planını Redaktə Et</DialogTitle>
          </DialogHeader>
          {selectedTariff && (
            <form onSubmit={handleUpdateTariff} className="space-y-4">
              <div>
                <Label>Tarif adı</Label>
                <Input
                  value={selectedTariff.name}
                  onChange={(e) => setSelectedTariff({ ...selectedTariff, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Sürət (Mbit/s)</Label>
                <Input
                  type="number"
                  value={selectedTariff.speed_mbps}
                  onChange={(e) => setSelectedTariff({ ...selectedTariff, speed_mbps: parseInt(e.target.value) || 0 })}
                  required
                />
              </div>
              <div>
                <Label>Qiymət (AZN)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={selectedTariff.price_azn}
                  onChange={(e) => setSelectedTariff({ ...selectedTariff, price_azn: parseFloat(e.target.value) || 0 })}
                  required
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="tariff_active"
                  checked={selectedTariff.is_active}
                  onChange={(e) => setSelectedTariff({ ...selectedTariff, is_active: e.target.checked })}
                  className="rounded"
                />
                <Label htmlFor="tariff_active">Aktiv</Label>
              </div>
              <Button type="submit" className="w-full bg-purple-600 hover:bg-purple-700">
                Yadda Saxla
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Create Equipment Dialog */}
      <Dialog open={showCreateEquipment} onOpenChange={setShowCreateEquipment}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Yeni Avadanlıq Seçimi</DialogTitle>
            <DialogDescription>Müqavilə üçün avadanlıq seçimi əlavə edin</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateEquipment} className="space-y-4">
            <div>
              <Label>Kateqoriya</Label>
              <Select value={newEquipment.category} onValueChange={(v) => setNewEquipment({ ...newEquipment, category: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EQUIPMENT_CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Avadanlıq adı</Label>
              <Input
                value={newEquipment.name}
                onChange={(e) => setNewEquipment({ ...newEquipment, name: e.target.value })}
                placeholder="məs: Huawei HG8245H"
                required
              />
            </div>
            <div>
              <Label>Xüsusiyyətlər</Label>
              <Input
                value={newEquipment.specs}
                onChange={(e) => setNewEquipment({ ...newEquipment, specs: e.target.value })}
                placeholder="məs: 2.4 GHz + 5 GHz"
              />
            </div>
            <div>
              <Label>Qiymət (AZN) - 0 = pulsuz</Label>
              <Input
                type="number"
                step="0.01"
                value={newEquipment.price_azn}
                onChange={(e) => setNewEquipment({ ...newEquipment, price_azn: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <Button type="submit" className="w-full bg-purple-600 hover:bg-purple-700">
              Yarat
            </Button>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Edit Equipment Dialog */}
      <Dialog open={showEditEquipment} onOpenChange={setShowEditEquipment}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Avadanlıq Seçimini Redaktə Et</DialogTitle>
          </DialogHeader>
          {selectedEquipment && (
            <form onSubmit={handleUpdateEquipment} className="space-y-4">
              <div>
                <Label>Avadanlıq adı</Label>
                <Input
                  value={selectedEquipment.name}
                  onChange={(e) => setSelectedEquipment({ ...selectedEquipment, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Xüsusiyyətlər</Label>
                <Input
                  value={selectedEquipment.specs || ''}
                  onChange={(e) => setSelectedEquipment({ ...selectedEquipment, specs: e.target.value })}
                />
              </div>
              <div>
                <Label>Qiymət (AZN)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={selectedEquipment.price_azn}
                  onChange={(e) => setSelectedEquipment({ ...selectedEquipment, price_azn: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <Button type="submit" className="w-full bg-purple-600 hover:bg-purple-700">
                Yadda Saxla
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
